/**
 * useJukeboxPlayback — "Your Own Tavern" Phase B client playback driver.
 *
 * Given the current jukebox state (from useLobbySocket), this hook:
 *   - plays the now-playing record's mood clip for EVERYONE when `playing`
 *     changes to a new pick (join-in-progress: starts at the right offset;
 *     skips a clip that's already essentially over);
 *   - drives advancement — the HOST fires `jukebox.advance` when the 12s
 *     clip window elapses; NON-hosts fire a fallback advance ~1s past the
 *     20s stale window so an absent host can't wedge the queue.
 *
 * All timers are torn down on unmount / room-leave / when the pick changes,
 * so we never advance after leaving or double-play a clip.
 */
import { useEffect, useRef } from 'react';
import { audio, type Mood } from '@tt/audio';
import {
  moodForRecord, JUKEBOX_CLIP_MS, JUKEBOX_STALE_JOIN_MS, JUKEBOX_STALE_ADVANCE_MS,
  type JukeboxState,
} from '@tt/lobby/records';

interface Args {
  jukebox: JukeboxState | null;
  isHost: boolean;
  /** True only in the waiting room (jukebox is a lobby affordance — we don't
   *  layer clips over in-match music). */
  active: boolean;
  sendJukeboxAdvance: () => void;
}

export function useJukeboxPlayback({ jukebox, isHost, active, sendJukeboxAdvance }: Args): void {
  /** Key of the pick we last acted on, so we play/advance exactly once per
   *  distinct now-playing entry. */
  const handledKeyRef = useRef<string | null>(null);
  const clipRef = useRef<{ stop(): void } | null>(null);
  const advanceTimerRef = useRef<number | null>(null);
  // Keep the latest advance sender + host flag in refs so the effect below
  // doesn't re-run (and re-fire timers) on every render.
  const advanceRef = useRef(sendJukeboxAdvance);
  advanceRef.current = sendJukeboxAdvance;
  const isHostRef = useRef(isHost);
  isHostRef.current = isHost;

  useEffect(() => {
    const clearTimer = (): void => {
      if (advanceTimerRef.current !== null) { window.clearTimeout(advanceTimerRef.current); advanceTimerRef.current = null; }
    };

    const playing = active ? jukebox?.playing : undefined;

    // Idle (no pick, or left the waiting room): stop any clip + timer, reset.
    if (!playing) {
      clearTimer();
      if (clipRef.current) { clipRef.current.stop(); clipRef.current = null; }
      handledKeyRef.current = null;
      return;
    }

    const key = `${playing.charId}|${playing.recordId}|${playing.startedAt}`;
    const now = Date.now();

    // Start the clip exactly once per distinct pick (unless it's essentially
    // over — a very late join).
    if (key !== handledKeyRef.current) {
      handledKeyRef.current = key;
      if (clipRef.current) { clipRef.current.stop(); clipRef.current = null; }
      const offset = now - playing.startedAt;
      const mood = moodForRecord(playing.recordId);
      if (mood && offset <= JUKEBOX_STALE_JOIN_MS) {
        clipRef.current = audio.playMoodClip(mood as Mood, { offsetMs: Math.max(0, offset) });
      }
    }

    // (Re)arm advancement on EVERY run — the target is absolute (derived from
    // startedAt), so re-arming is idempotent. This must NOT be skipped on the
    // same-pick path: any queue-only jukebox.state broadcast re-runs this
    // effect, and an early return after a cleanup would strand the queue with
    // no client left holding a live advance timer. Host advances at the
    // clip's natural end; a non-host arms the host-asleep fallback just past
    // the 20s stale window (isHost is a dep, so host migration retargets).
    clearTimer();
    const targetMs = isHostRef.current
      ? playing.startedAt + JUKEBOX_CLIP_MS
      : playing.startedAt + JUKEBOX_STALE_ADVANCE_MS + 1000;
    advanceTimerRef.current = window.setTimeout(() => {
      advanceTimerRef.current = null;
      advanceRef.current();
    }, Math.max(0, targetMs - now));
  }, [jukebox, active, isHost]);

  // Unmount safety — stop any lingering clip + timer when the hook goes away.
  useEffect(() => () => {
    if (clipRef.current) { clipRef.current.stop(); clipRef.current = null; }
    if (advanceTimerRef.current !== null) { window.clearTimeout(advanceTimerRef.current); advanceTimerRef.current = null; }
  }, []);
}
